import type { components } from '@pba/types-openapi-client';
import { parseISO } from 'date-fns';
import type { GivingInterval, GivingStatus, GivingType, Tag, UploadedFile } from 'models';
import type { Entity } from '../entity';
import { createUploadedFile } from '../uploaded-file';
import { GivingTypeEnum } from './giving-type';

export type GivingBase = {
  amountGoal?: number;
  amountTotal: number;
  coverPhoto?: UploadedFile;
  endTime?: Date;
  giveNow: boolean;
  giversCount: number;
  profilePhoto?: UploadedFile;
  startTime: Date;
  status: GivingStatus;
  type: GivingTypeEnum;
} & Entity;

export type GivingDetail = {
  address?: {
    apartment?: string;
    city: string;
    country: string;
    state?: string;
    street: string;
    zip: string;
  };
  amounts: number[];
  featuredInChannels: boolean;
  htmlDescription: string;
  intervals: GivingInterval[];
  pinned: boolean;
  prices: components['schemas']['GivingPrices'];
  showInChannels: boolean;
  tags?: Tag[];
  textDescription: string;
} & GivingBase;

const givingTypeToEnum = (value: GivingType): GivingTypeEnum => {
  switch (value) {
    case 'crowdfunding':
      return GivingTypeEnum.CROWDFUNDING;
    case 'donation':
      return GivingTypeEnum.DONATION;
    case 'offering':
      return GivingTypeEnum.OFFERING;
    case 'tithe':
      return GivingTypeEnum.TITHE;
  }
};

export const createGivingBase = (giving: components['schemas']['GivingBase']): GivingBase => ({
  amountGoal: giving.amount_goal,
  amountTotal: giving.amount_total,
  coverPhoto: giving.cover_photo ? createUploadedFile(giving.cover_photo) : undefined,
  endTime: giving.end_time ? parseISO(giving.end_time) : undefined,
  giveNow: giving.give_now,
  giversCount: giving.givers_count,
  id: giving.id,
  name: giving.name,
  startTime: parseISO(giving.start_time),
  status: giving.status,
  type: givingTypeToEnum(giving.giving_type),
});

export const createGivingDetail = (giving: components['schemas']['GivingDetail']): GivingDetail => ({
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  ...createGivingBase(giving),
  address: giving.address ? { ...giving.address, zip: giving.address.zip_code } : undefined,
  amounts: giving.amounts,
  featuredInChannels: giving.featured_in_channels,
  htmlDescription: giving.html_content,
  intervals: giving.intervals,
  pinned: giving.pinned,
  prices: giving.prices,
  showInChannels: giving.show_in_channels,
  tags: giving.tags,
  textDescription: giving.text_content,
});
